import { useState } from 'react';
import { useRouter } from 'next/router';
import validate from 'validate.js';

import {
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Paragraph,
  Text,
  Textarea,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import {
  useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_create,
  useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_delete,
  useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { removeErrorKey } from '../../../utils/formValidation';

import { costsAndFeesConstraints } from '../../../../srcLegacy/shared/validation';
import { emitClickSaveDisclaimerEvent, EVENT_IDS } from '../../../analytics/events/multiFamilyCostsAndFees';
import { useAuth } from '../../AuthProvider';
import ModalFooter from '../ModalFooter';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

type Props = {
  disclaimerText: string | null;
  setShowCustomDisclaimerModal: (show: boolean) => void;
};

const fieldName = 'disclaimerText';

const CustomDisclaimerModal = ({ disclaimerText = null, setShowCustomDisclaimerModal }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { enqueueToast } = useToast();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [content, setContent] = useState<string | null>(disclaimerText);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const validateField = (fieldValue: string) => {
    const error = validate(
      { [fieldName]: fieldValue },
      { [fieldName]: (costsAndFeesConstraints.values?.[fieldName] || {}) as Record<string, unknown> },
    )?.[fieldName]?.[0];

    if (error) {
      const errorMessage = removeErrorKey(fieldName, error);
      setError(errorMessage);
      return;
    }

    setError(null);
  };

  const { mutate: createDisclaimer, isPending: isCreating } =
    useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_create({
      onSuccess: () => {
        setShowCustomDisclaimerModal(false);
        enqueueToast(<Toast tone="success">Disclaimer added</Toast>);
      },
      onError: (error) => {
        enqueueToast(
          <Toast tone="critical">
            {(error as DatarouterError).message ||
              'There was an error saving your disclaimer. Refresh the page and try again.'}
          </Toast>,
        );
      },
      meta: {
        invalidates: ['mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'create costs and fees disclaimer' },
      },
    });

  const { mutate: updateDisclaimer, isPending: isUpdating } =
    useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_update({
      onSuccess: () => {
        setShowCustomDisclaimerModal(false);
        enqueueToast(<Toast tone="success">Disclaimer updated</Toast>);
      },
      onError: (error) => {
        enqueueToast(
          <Toast tone="critical">
            {(error as DatarouterError).message ||
              'There was an error saving your changes. Refresh the page and try again.'}
          </Toast>,
        );
      },
      meta: {
        invalidates: ['mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'update costs and fees disclaimer' },
      },
    });

  const { mutate: deleteDisclaimer, isPending: isDeleting } =
    useRentalManagerUserMultifamilyCostAndFeesDisclaimerWebApiHandler_delete({
      onSuccess: () => {
        setShowCustomDisclaimerModal(false);
        enqueueToast(<Toast tone="success">Disclaimer deleted</Toast>);
      },
      onError: (error) => {
        enqueueToast(
          <Toast tone="critical">
            {(error as DatarouterError).message ||
              'There was an error deleting your disclaimer. Refresh the page and try again.'}
          </Toast>,
        );
      },
      meta: {
        invalidates: ['mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'delete costs and fees disclaimer' },
      },
    });

  const isEditingDisclaimer = disclaimerText !== null;

  const handleSaveClick = () => {
    if (listingDetails && user) {
      emitClickSaveDisclaimerEvent({ currentProperty: listingDetails, user });
    }

    if (isEditingDisclaimer) {
      updateDisclaimer({
        postData: {
          disclaimerText: content as string,
          listingAlias,
        },
      });
    } else {
      createDisclaimer({
        postData: {
          listingAlias,
          disclaimerText: content as string,
        },
      });
    }
  };

  const handleDeleteClick = () => {
    deleteDisclaimer({
      getParams: { listingAlias },
    });
  };

  const handleClose = () => {
    setShowCustomDisclaimerModal(false);
  };

  const handleContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setContent(value);
    validateField(value);
  };

  const renderFooter = () => {
    if (isEditingDisclaimer) {
      return (
        <ButtonGroup aria-label="disclaimer actions">
          <Button
            aria-label="delete"
            tone="critical"
            emphasis="filled"
            size="sm"
            onClick={handleDeleteClick}
            disabled={isDeleting}
          >
            Delete
          </Button>
          <Button
            aria-label="save"
            tone="brand"
            emphasis="filled"
            size="sm"
            onClick={handleSaveClick}
            data-eventid={EVENT_IDS.CLICK_SAVE_DISCLAIMER}
            disabled={isUpdating || error !== null}
          >
            Save changes
          </Button>
        </ButtonGroup>
      );
    }

    return (
      <ModalFooter
        submitText="Save"
        cancelText="Cancel"
        isSubmitDisabled={error !== null}
        isSubmitting={isCreating || isUpdating}
        onCancel={handleClose}
        onSubmit={handleSaveClick}
        onSubmitDataEventId={EVENT_IDS.CLICK_SAVE_DISCLAIMER}
      />
    );
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      header={<Heading level={2}>Add a disclaimer</Heading>}
      body={
        <div data-testid="CustomDisclaimerModal">
          <Text>
            This is displayed at the bottom of the list of costs and fees on your listing. If you don&apos;t add one,
            Zillow&apos;s standard disclaimer will be displayed.
          </Text>

          <Box css={{ marginTop: '600' }}>
            <FormField
              required={true}
              label={<Label>Disclaimer text</Label>}
              error={!!error || Boolean(content && content.length > 1000)}
              control={
                <Textarea
                  name={fieldName}
                  value={content || ''}
                  onChange={handleContentChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
              }
              description={
                error !== null ? (
                  <FormHelp>{error}</FormHelp>
                ) : (
                  <CharacterCountFormHelp
                    currentLength={(content || '').length}
                    maxLength={1000}
                    isFocused={isFocused}
                    label="Disclaimer text"
                  />
                )
              }
            />
          </Box>
          <Box css={{ marginTop: '600' }}>
            <Text textStyle="body-sm-bold">Standard disclaimer text</Text>
            <Paragraph css={{ marginTop: '400' }}>
              All pricing and fees information is provided by the housing provider.
            </Paragraph>
          </Box>
        </div>
      }
      footer={renderFooter()}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default CustomDisclaimerModal;
